/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { useMemo } from 'react';
import { isNil } from 'lodash';

import { isMutable } from '@broadleaf/admin-components/dist/view/named-components/view-types/EntityView/utils';
import { useContextParams } from '@broadleaf/admin-components/dist/view/named-components/view-types/EntityView/hooks';
import { successFetch } from '@broadleaf/admin-components/dist/view/named-components/view-types/EntityView/state';
import { IViewCommonAction } from '@broadleaf/admin-components/dist/types/view';
import {
  isCatalogDiscriminated,
  isProfileDiscriminated
} from '@broadleaf/admin-components/dist/tracking/utils/CatalogUtils';
import { request } from '@broadleaf/admin-components/dist/metadata/utils/request';
import {
  FormComponents,
  ModalFormAction
} from '@broadleaf/admin-components/dist/form';
import { log, mapper } from '@broadleaf/admin-components/dist/common';
import CatalogSelectorIfNecessary from '@broadleaf/admin-components/dist/tracking/components/CatalogSelectorIfNecessary';

import SecondaryAction from '../SecondaryAction';
import { setIn } from '@broadleaf/admin-components/dist/common/utils/lodash';
import { hooks, unstable } from '@broadleaf/admin-components';
const { useEventCallback } = hooks;
const { MetadataUtils } = unstable;
const { getAttribute, getEndpointByType } = MetadataUtils;

const logger = log.getLogger('view.named-components.view-types.Entity');

/**
 * This action is used to copy an Entity. It opens a modal dialog
 * with additional configuration.
 */
export const WhitelabelCopy: React.FC<CopyProps> = ({
  actionDefinition,
  className,
  dispatch,
  formik,
  metadata,
  state
}) => {
  const isDisabled = !isMutable(formik, state);
  const handleSubmit = useHandleSubmit({
    actionDefinition,
    dispatch,
    metadata,
    state
  });
  const initialValues = useInitialValues({
    actionDefinition,
    state
  });
  return (
    <ModalFormAction
      // @ts-ignore
      className="tw-w-full"
      components={actionDefinition.components}
      renderFormComponents={({ components, formik, readOnly }) => {
        return (
          <>
            <CatalogSelectorIfNecessary
              name={'catalogId'}
              formik={formik}
              catalogDiscriminated={isCatalogDiscriminated(metadata)}
              profileDiscriminated={isProfileDiscriminated(metadata)}
              readOnly={readOnly}
              searchGroupDiscriminated={false}
            />
            <FormComponents
              components={components}
              formik={formik}
              readOnly={readOnly}
            />
          </>
        );
      }}
      initialValues={initialValues}
      key={actionDefinition.type}
      onSubmit={handleSubmit}
      renderAction={({ toggleOpen }) => (
        <SecondaryAction
          className={className}
          disabled={isDisabled}
          onClick={toggleOpen}
        >
          {actionDefinition.label}
        </SecondaryAction>
      )}
      submitLabel={getAttribute(actionDefinition, 'submitLabel')}
      title={actionDefinition.label}
      tooltipPlacement={'top'}
    />
  );
};

export interface CopyProps extends IViewCommonAction {
  className?: string;
}

function useHandleSubmit({ actionDefinition, dispatch, metadata, state }) {
  const copyConfig =
    getEndpointByType(actionDefinition, 'COPY_WHITELABEL') ||
    getEndpointByType(metadata, 'COPY_WHITELABEL');
  const contextParams = useContextParams(metadata, state);

  return useEventCallback(
    async values => {
      const { catalogId, ...requestData } = values;
      const context = !isNil(catalogId)
        ? setIn(contextParams, 'tracking.catalog.currentCatalogId', catalogId)
        : contextParams;

      try {
        const { data } = await request(
          {
            ...copyConfig,
            data: requestData
          },
          {
            ...context,
            values: requestData
          }
        );

        dispatch(successFetch(data));
      } catch (error) {
        logger.error('An error occurred while copying the Entity: ', error);
        throw error;
      }
    },
    [copyConfig, contextParams, dispatch]
  );
}

function useInitialValues({ actionDefinition, state }) {
  const transformCopyMapping = getAttribute(actionDefinition, 'transformCopy');

  return useMemo(() => {
    const source = { ...state.data };
    return mapper.map(source, transformCopyMapping, {
      destination: source
    });
  }, [state.data, transformCopyMapping]);
}

export default WhitelabelCopy;
